// @ts-check
import { useRef, useState } from "react";
import { cx } from "~utils";

import { useAuth } from "~features/auth";
import { useEmailCollector } from "~features/email/use-email-collector";
import { pushDataLayer } from "~lib/analytics/datalayer";

import { CheckboxLabelCopy } from "~features/email/legal";
import { DynamicLabelInput } from "~ui/DynamicLabelInput";
import { Input } from "~ui/Input";

import styles from "./NewsletterSignup.module.css";
import newsletterSignupArt from "./newsletterSignUpArt.jpg";

const IconCheckSuccess = () => (
  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" className={styles.icon}>
    <path
      d="M6.75 12.25L1.75 7.25L0.25 8.75L6.75 15.25L18.25 3.75L16.75 2.25L6.75 12.25Z"
      fill="currentColor"
    />
  </svg>
);

export default function NewsletterSignup() {
  const formRef = useRef(null);
  const emailInputRef = useRef(null);
  const { login } = useAuth();

  const [email, setEmail] = useState(null);

  // Hide error messages when user starts typing
  const [dismissError, setDismissError] = useState(false);

  // Form handling
  const { api, collectorError, collectorResponse, subscribe } = useEmailCollector();
  let formStatus = "idle";
  if (api.loading) formStatus = "loading";
  else if (collectorResponse?.action === "success") formStatus = "success";
  else if (collectorError) formStatus = "error";

  const handleFormSubmit = (e) => {
    e.preventDefault();
    submitForm();
  };
  const submitForm = async (ignore_suggestion = false) => {
    const formData = new FormData(formRef.current);
    const emailValue = formData.get("email").toString();
    setEmail(emailValue);
    setDismissError(false);
    const result = await subscribe({
      email: emailValue,
      lists: ["today_in_science"],
      ignore_suggestion,
    });

    if ("action" in result && result.action === "success") {
      /* Do not use "newsletter-sign-up" DL event for signups. */
      pushDataLayer({
        event: "email-obtained-newsletter-signup",
        user: { newsletters: "today_in_science" },
      });
    }
  };

  return (
    <section className={styles.newsletterSignup} id="newsletter">
      <form
        className={cx(styles.newsletterSignupForm, styles["status-" + formStatus])}
        onSubmit={handleFormSubmit}
        ref={formRef}
      >
        <h2 className={styles.newsletterSignupMainText}>
          Sign up for the <i>Today in Science</i> Newsletter
        </h2>
        <p className={styles.newsletterSignUpDek}>Delivered free, every weekday</p>
        {/* @ts-expect-error */}
        <DynamicLabelInput ref={emailInputRef} onInput={() => setDismissError(true)} />

        {/* Error Message */}
        {!dismissError &&
          !api.loading &&
          (formStatus === "error" ||
            (collectorResponse?.action && collectorResponse?.action !== "success")) && (
            <div
              style={{
                marginTop: "0.5rem",
                fontSize: "1rem",
                color: "var(--color-red)",
              }}
            >
              {/* Network errors */}
              {collectorError && (
                <p>
                  {collectorError?.["message"] ||
                    collectorError?.["error"] ||
                    "There was a problem submitting your email. Please try again."}
                </p>
              )}

              {/* Typo */}
              {collectorResponse?.action === "suggest" && (
                <p>
                  Did you mean:{" "}
                  <a
                    href="#email"
                    className={styles.emailCollectorModalSignInLnk}
                    onClick={(e) => {
                      e.preventDefault();
                      emailInputRef.current.focus();
                      requestAnimationFrame(() => {
                        emailInputRef.current.value = collectorResponse.suggestion;
                      });
                      setDismissError(true);
                    }}
                  >
                    {collectorResponse.suggestion}
                  </a>
                  ?
                </p>
              )}

              {/* Hard rejections (e.g. disposable email domains) get a specific message */}
              {collectorResponse?.action === "reject" && <p>{collectorResponse.message}</p>}

              {/* Soft rejections (e.g. risky emails) get a generic message */}
              {collectorResponse?.action === "soft-reject" && (
                <p>
                  {collectorResponse.message} Try another email or{" "}
                  <a
                    href="/login/"
                    id="emailCollectorModalErrSignInLnk"
                    className={styles.emailCollectorModalSignInLnk}
                    onClick={(e) => {
                      e.preventDefault();
                      login();
                    }}
                  >
                    sign in
                  </a>
                  .
                </p>
              )}
            </div>
          )}

        <div className={styles.privacyCheckboxContainer}>
          <Input
            className={cx(styles.checkbox, styles.privacyCheckbox)}
            type="checkbox"
            id="privacy-policy"
            required
          />
          <label className={styles.newsletterSignupPrivacyLabel} htmlFor="privacy-policy">
            <CheckboxLabelCopy />
          </label>
        </div>

        {/* Submit */}
        <button
          disabled={formStatus === "loading" || formStatus === "success"}
          className={styles.newsletterSignupBtn}
          type="submit"
        >
          Sign Up
        </button>
      </form>

      {/* Visually hidden until successful submission */}
      <div className={cx(styles.message, styles["status-" + formStatus])}>
        <p>Thank you for signing up!</p>
        <div className={styles.inputContainer}>
          {/* Mock input */}
          <div className={cx("form-input", styles.input)}>
            <span>{email || "\u00A0" /* that's an &nbsp; */}</span>
            <IconCheckSuccess />
          </div>
        </div>
        <p>
          <b>
            <a href="/newsletters/">Check out our other newsletters</a>
          </b>
        </p>
      </div>

      <div className={styles.newsletterSignupImg}>
        <img
          src={newsletterSignupArt}
          role="presentation"
          alt=""
          loading="lazy"
          width={356}
          height={249}
        />
      </div>
    </section>
  );
}
